import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  formatPhoneNumber,
  normalizePhoneNumber,
  phoneDisplay,
  type MyPhoneLine,
  type PhoneContact,
} from '@avslack/shared';
import {
  getPhoneContacts,
  getPhoneRecent,
  identifyPhoneNumber,
  listCallableLines,
  listMyPhoneLines,
} from '../lib/api';

export const myPhoneLinesKey = ['phone', 'lines-mine'] as const;
export const phoneContactsKey = ['phone', 'contacts'] as const;
export const phoneRecentKey = ['phone', 'recent'] as const;

/**
 * A vonalak csatornai, amikben bent vagyok (lasd `MyPhoneLine`).
 *
 * A valtozasrol a szerver szol (`phone.lines.changed`), ezert a hosszu
 * `staleTime` nem jelent elavult oldalsavot.
 */
export function useMyPhoneLines() {
  return useQuery({ queryKey: myPhoneLinesKey, queryFn: listMyPhoneLines, staleTime: 60_000 });
}

/**
 * A vonal csatornajanak cime: "Telefon – Recepció (+36 1 220 4899)".
 *
 * A NEV az elso: tobb vonalnal a szamokat senki nem tudja fejbol, a
 * "Recepció" vagy "Ügyelet" viszont azonnal mondja, melyikrol van szo.
 */
export function phoneLineTitle(
  line: Pick<MyPhoneLine, 'label' | 'number'>,
  /** Az oldalsavban a telefon-ikon mar elmondja, hogy vonalrol van szo. */
  { prefix = true }: { prefix?: boolean } = {},
): string {
  const number = formatPhoneNumber(line.number);
  const label = line.label.trim();
  const name = label ? `${label} (${number})` : number;
  return prefix ? `Telefon – ${name}` : name;
}

/**
 * A ceg cimjegyzeke, szam szerint - es egy fuggveny, ami a szamot ugy adja
 * vissza, ahogy mutatni kell: `Kiss Anna (+36 30 123 4567)`, vagy csak a szam.
 */
export function usePhoneContacts() {
  const query = useQuery({
    queryKey: phoneContactsKey,
    queryFn: getPhoneContacts,
    staleTime: 30_000,
  });
  const byNumber = useMemo(
    () => new Map((query.data ?? []).map((contact) => [contact.number, contact])),
    [query.data],
  );

  const entryOf = (number: string | null | undefined): PhoneContact | null =>
    number ? (byNumber.get(normalizePhoneNumber(number)) ?? null) : null;

  /*
    A szemely ES a ceg egyutt, ha mindketto megvan: "Kiss Anna - Net Vet Kft.".
    Igy egy nev ala nem csuszik ossze ket rendelo ugyanolyan nevu asszisztense,
    es a hivas naplojaban is latszik, honnan hivtak.
  */
  const nameOf = (number: string | null | undefined): string | null => {
    const entry = entryOf(number);
    if (!entry) return null;
    if (entry.name && entry.companyName) return `${entry.name} – ${entry.companyName}`;
    return entry.name || entry.companyName || null;
  };

  const display = (number: string | null | undefined): string =>
    phoneDisplay(number, nameOf(number));

  return { contacts: query.data ?? [], nameOf, display, entryOf };
}

/**
 * KI HIV? - a TT ugyfelkartyaja egy szamhoz.
 *
 * A csengetes NEM var erre: a hivas a maga utjan megy, a kartya utolag erkezik.
 * Ezert nincs ujraprobalkozas sem - ha a TT eppen nem valaszol, a kartya
 * kimarad, es a kolléga a szamot latja, ahogy eddig.
 */
export const phoneIdentityKey = (number: string) => ['phone', 'identify', number] as const;

export function usePhoneIdentity(number: string | null | undefined) {
  return useQuery({
    queryKey: phoneIdentityKey(number ?? ''),
    queryFn: () => identifyPhoneNumber(number!),
    enabled: Boolean(number),
    staleTime: 120_000,
    retry: false,
  });
}

/** A korabbi hivott es hivo szamok - a cimjegyzek megnyitasakor frissen. */
export function usePhoneRecent(enabled: boolean) {
  return useQuery({ queryKey: phoneRecentKey, queryFn: getPhoneRecent, enabled, staleTime: 0 });
}

export const callableLinesKey = ['phone', 'callable'] as const;

/** A vonalak (szamok), amikrol en hivhatok kifele - a tarcsazo szamvalasztoja. */
export function useCallableLines(enabled = true) {
  return useQuery({
    queryKey: callableLinesKey,
    queryFn: listCallableLines,
    enabled,
    staleTime: 30_000,
  });
}

const LAST_LINE_KEY = 'avslack:last-phone-line';

/** Az utoljara hasznalt vonal - ez az alapertelmezes a valasztoban. */
export function lastUsedLine(): string | null {
  try {
    return localStorage.getItem(LAST_LINE_KEY);
  } catch {
    return null;
  }
}

export function rememberLine(lineId: string): void {
  try {
    localStorage.setItem(LAST_LINE_KEY, lineId);
  } catch {
    // Tarolo nelkul legfeljebb nem emlekszik - a valaszto akkor is mukodik.
  }
}
